/**
 * Guard de chunk obsoleto (BUG-37).
 *
 * Depois de um deploy, um cliente que ainda tem o `index.html` antigo em cache
 * pede chunks com hash que não existem mais e a rota lazy morre com
 * "Failed to fetch dynamically imported module". Com PWA + service worker isso
 * é rotina pós-deploy, não exceção. Um reload resolve, porque busca o
 * `index.html` novo com os hashes atuais.
 *
 * O contador em `sessionStorage` é o que impede o loop: se duas tentativas
 * dentro de 60s não resolveram, para de recarregar e deixa o ErrorBoundary
 * aparecer — melhor uma tela de erro do que a página piscando pra sempre.
 */

const STORAGE_KEY = "livra_chunk_reload";
const MAX_ATTEMPTS = 2;
const WINDOW_MS = 60_000;

const CHUNK_ERROR_RE =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|loading chunk \S+ failed/i;

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_ERROR_RE.test(message);
}

interface Attempts {
  n: number;
  first: number;
}

function readAttempts(): Attempts {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { n: 0, first: 0 };
    const parsed: unknown = JSON.parse(raw);
    const candidate = parsed as Partial<Attempts>;
    if (typeof candidate?.n !== "number" || typeof candidate?.first !== "number") {
      return { n: 0, first: 0 };
    }
    return { n: candidate.n, first: candidate.first };
  } catch {
    return { n: 0, first: 0 };
  }
}

/** Teto da espera pelo service worker novo antes de recarregar mesmo assim. */
export const SW_REFRESH_TIMEOUT_MS = 3_000;

// BUG-322: entre pedir o SW novo e o reload a página continua viva, e o
// `lazy()` que falhou ainda estoura no React. Enquanto isto for `true`, o
// ErrorBoundary trata qualquer erro como "atualizando", sem tela de erro.
let reloadPending = false;

export function isStaleReloadPending(): boolean {
  return reloadPending;
}

export function __resetChunkReloadForTests(): void {
  reloadPending = false;
}

/**
 * BUG-322: o reload sozinho não basta quando um service worker controla a
 * página — a navegação volta a ser servida pelo `app-shell.html` ANTIGO do
 * precache, com os mesmos hashes mortos, e queima as tentativas. No desktop é
 * a regra: os chunks `*Desktop-*` ficam fora do precache (`globIgnores`) e vêm
 * da rede, onde o deploy já os apagou. Então: pede o SW novo (`update()`),
 * espera ele assumir (`controllerchange`, com `autoUpdate` = skipWaiting +
 * clientsClaim) até `SW_REFRESH_TIMEOUT_MS`, e só então recarrega.
 */
async function refreshServiceWorkerThenReload(): Promise<void> {
  try {
    const sw = navigator.serviceWorker;
    const registration = await sw.getRegistration();
    if (registration) {
      const tookOver = new Promise<void>((resolve) => {
        sw.addEventListener("controllerchange", () => resolve(), { once: true });
      });
      const timeout = new Promise<void>((resolve) => {
        setTimeout(resolve, SW_REFRESH_TIMEOUT_MS);
      });
      await Promise.race([registration.update().then(() => tookOver), timeout]);
    }
  } catch {
    // Sem SW atualizável o caminho é o de sempre: recarregar.
  }
  window.location.reload();
}

/**
 * Recarrega a página para buscar o `index.html` novo.
 *
 * @returns `true` se disparou o reload; `false` se as tentativas da janela já
 * se esgotaram — nesse caso o chamador deve mostrar o erro em vez de insistir.
 */
export function reloadForStaleChunk(): boolean {
  // Já há um reload a caminho (esperando o SW): não gasta outra tentativa.
  if (reloadPending) return true;

  const now = Date.now();
  const previous = readAttempts();
  const attempts: Attempts =
    now - previous.first > WINDOW_MS ? { n: 0, first: now } : previous;

  if (attempts.n >= MAX_ATTEMPTS) return false;

  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ n: attempts.n + 1, first: attempts.first || now }),
    );
  } catch {
    // Sem sessionStorage não há como contar tentativas — não arrisca o loop.
    return false;
  }

  if (typeof navigator !== "undefined" && navigator.serviceWorker?.controller) {
    reloadPending = true;
    void refreshServiceWorkerThenReload();
    return true;
  }

  window.location.reload();
  return true;
}

export function initChunkReloadGuard(): void {
  if (typeof window === "undefined") return;

  // O Vite emite este evento quando o preload de um chunk falha, antes de
  // qualquer boundary do React ver o erro. É o ponto mais cedo de captura.
  // BUG-322: `preventDefault()` faz o helper do Vite ENGOLIR o erro e o import
  // resolver `undefined` (o `lazy()` estoura com "reading 'default'"). Só vale
  // quando o reload vai mesmo acontecer; esgotadas as tentativas, o erro de
  // chunk segue até o ErrorBoundary, que mostra "Tentar de novo".
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadForStaleChunk()) event.preventDefault();
  });
}
